import { Badge, Card, Divider, Group, SimpleGrid, Text, Title } from '@mantine/core';

import type { QualityFormulaPreviewResult } from '../qualityFormulaTypes';

import { ResultField } from './ResultField';
import { formatCurrency, formatNumber, getDirectionColor, getTotalImpactColor } from './utils/resultFormatting';

interface ImpactCardProps {
  result: QualityFormulaPreviewResult;
}

const formatOptionalNumber = (value: null | number | undefined): string =>
  value === null || value === undefined ? '—' : formatNumber(value);

const PriceImpactFields: React.FC<{ directionColor: string; result: QualityFormulaPreviewResult }> = ({
  directionColor,
  result,
}) => (
  <>
    <ResultField label="Matched Tier" value={result.matchedTier === null ? '—' : `#${result.matchedTier}`} />
    <ResultField
      color={directionColor}
      label="Price Change / Unit"
      value={result.unitImpact === null ? '—' : formatCurrency(result.unitImpact)}
    />
  </>
);

const QuantityImpactFields: React.FC<{ result: QualityFormulaPreviewResult }> = ({ result }) => (
  <>
    <ResultField label="Delivered Qty" value={formatOptionalNumber(result.shippedQuantity)} />
    <ResultField label="New Qty" value={formatOptionalNumber(result.adjustedInvoiceQuantity)} />
  </>
);

// Renders a single price- or quantity-impact result. Kind-specific fields are the only branch —
// everything else (badge, title, description, total) is shared markup for both kinds.
export const ImpactCard: React.FC<ImpactCardProps> = ({ result }) => {
  const isPrice = result.kind === 'price';
  const directionColor = getDirectionColor(result.direction);

  return (
    <Card padding="lg" radius="md" shadow="sm" withBorder>
      <Group mb="xs">
        <Badge color={isPrice ? 'blue' : 'orange'} variant="light">
          {isPrice ? 'Price Impact' : 'Quantity Impact'}
        </Badge>
      </Group>
      <Title mb={4} order={3} size="h5">
        {result.specLabel}
      </Title>
      <Text c="dimmed" fz="xs" mb="sm">
        {result.description}
      </Text>
      <SimpleGrid cols={2} spacing="sm">
        <ResultField label="Formula Applied" value={result.formulaLabel} />
        {result.incrementLabel ? <ResultField label="Increment" value={result.incrementLabel} /> : null}
        <ResultField label="Test Value" value={formatNumber(result.testValue)} />
        <ResultField label="Contracted Value" value={formatNumber(result.contractedValue)} />
        {isPrice ? (
          <PriceImpactFields directionColor={directionColor} result={result} />
        ) : (
          <QuantityImpactFields result={result} />
        )}
      </SimpleGrid>
      {result.totalImpact === null ? null : (
        <>
          <Divider my="sm" />
          <Group justify="space-between">
            <Text c="dimmed" fw={600} fz={11} lts={0.3} tt="uppercase">
              {isPrice ? 'Total Price Impact' : 'Quantity Reduced'}
            </Text>
            <Text c={getTotalImpactColor(result)} fw={700} fz="md">
              {isPrice ? formatCurrency(result.totalImpact) : formatNumber(result.totalImpact)}
              {result.kind === 'quantity' ? ' units' : ''}
            </Text>
          </Group>
        </>
      )}
    </Card>
  );
};
